import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import * as FileGenerateUploadUrl from '../../server/routes/files-generate-upload-url';
import * as FileDelete from '../../server/routes/files/delete';
import { generatePath } from '../../server/services/utils.service';
import type { FileElement } from '../../server/types/file-types';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private http = inject(HttpClient);
  private utilsService = inject(UtilsService);

  uploadFile({ file, orgId }: { file: File; orgId: string }): {
    progressObservable: ReplaySubject<number>;
    messageFileObservable: Subject<FileElement>;
  } {
    const progressObservable = new ReplaySubject<number>(1);
    const messageFileObservable = new Subject<FileElement>();
    progressObservable.next(0);
    const generateUploadUrlRequest: FileGenerateUploadUrl.ReqBody = {};

    const path = generatePath<FileGenerateUploadUrl.ReqParams>(
      '/api/orgs/:orgId/files-generate-upload-url',
      { orgId }
    );
    this.http
      .post<FileGenerateUploadUrl.ResBody>(path, generateUploadUrlRequest)
      .subscribe(async ({ messageFile }) => {
        // Image service upload
        const { _id: fileId } = messageFile;
        const fileSize = file.size;
        let confirmedUploadSizeEstimate = 0;

        let errorOccurred = false;
        const fileData = await file.text();

        const messageFileElement: FileElement = {
          _id: fileId,
          name: file.name,
          size: fileSize,
        };

        const pushFileToServer = async ({
          fileData: inputFileData,
          size,
          signedUrl,
        }: {
          fileData: string;
          size: number;
          signedUrl: string;
        }) => {
          // Upload the file to AWS
          this.http
            .put(signedUrl, inputFileData, {
              reportProgress: true,
              observe: 'events',
            })
            .subscribe({
              next: async (event) => {
                if (event.type === HttpEventType.UploadProgress) {
                  // This is an upload progress event. Compute partial progress and update progress
                  if (event.total) {
                    const thisFileProgressEstimate =
                      fileSize * (event.loaded / event.total);
                    const proportion =
                      (thisFileProgressEstimate + confirmedUploadSizeEstimate) /
                      fileSize;
                    progressObservable.next(Math.round(100 * proportion));
                  }
                } else if (event instanceof HttpResponse) {
                  // Update confirmed upload size and update progress
                  confirmedUploadSizeEstimate += size;
                  const proportion = confirmedUploadSizeEstimate / fileSize;
                  progressObservable.next(Math.round(100 * proportion));

                  // Finalize the upload
                  // Wait manually if local to allow image processing
                  if (this.utilsService.isLocalSafe()) {
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                  }
                  messageFileObservable.next(messageFileElement);
                }
              },
              error: (error) => {
                errorOccurred = true;
                this.deleteFile({ orgId, fileId });

                messageFileObservable.error(error);
              },
            });
        };

        // Upload the main file
        pushFileToServer({
          fileData,
          size: fileSize,
          signedUrl: messageFile.uploadUrl,
        });
      });
    return { messageFileObservable, progressObservable };
  }

  deleteFile({ orgId, fileId }: { orgId: string; fileId: string }): void {
    const path = generatePath<FileDelete.ReqParams>(
      '/api/orgs/:orgId/files/:fileId',
      { orgId, fileId }
    );
    this.http.delete(path).subscribe(() => {
      // File should be deleted on the server.
    });
  }
}
